"use client"

import { useState, useEffect, useRef } from "react"
import "./UIProcess.css"

const ProductDesignProcess = () => {
  const [activeTab, setActiveTab] = useState("research") // Default to 'research' tab
  const headerTitleRef = useRef(null)
  const topParagraphRef = useRef(null)
  const mainDescriptionRef = useRef(null)
  const imageRef = useRef(null)
  const tabsRef = useRef(null)

  const tabContent = {
    research: {
      title: "BOOTSTRAP",
      listItems: ["1. BRIEFING", "2. COMPONENTS LIBRARY", ],
      paragraphs: [
        "The first step of the IPD is the Briefing and it involves presenting the project's vision and goals, clarifying business requirements, and identifying competitors and complementary products. We also explore high-level personas and ask relevant questions. This step leverages existing knowledge and ensures differentiation.The Components Library is where we establish the visual style of the application. We define the color palette, fonts, input fields, buttons, and other elements. We then create reusable components for designing screens and features, ensuring consistency throughout the application. This living document maintains visual congruency and improves efficiency in the design process.",
      ],
    },
    ideation: {
      title: "DESIGN SPRINT 1...N",
      listItems: ["1. MOCKUP", "2. GUI DESIGN", "3. USER FLOW"],
      paragraphs: [
        "The Mockup deliverable focuses on creating basic visuals of the features or screens. This involves gathering requirements from key stakeholders and creating visual representations of the concepts. It also includes identifying any missing content such as icons, copy, videos, or images. The main goal of this step is to validate the concept and address the user flow and information architecture, without emphasizing visual aesthetics. This reduces implementation time by making smarter decisions, thus resulting in faster iterations.",

"For the GUI Design, we execute the final-looking screens by applying the established style to the mockups and incorporating the necessary content. This brings the visual design to life and ensures that the screens have a consistent and visually appealing appearance. By integrating the defined style and content, we create an engaging user interface that aligns with the overall design goals.",

"In the User Flow step, we map out the path that users should follow to use the feature effectively. This involves defining each action that should follow a click or interaction. By carefully planning the user flow, we ensure a clear understanding of the feature for both the design and development teams. This step helps establish a seamless and intuitive user experience by guiding users through the desired actions and interactions.",
      ],
    },
    execution: {
      title: "BACKLOG",
      listItems: ["1. PRODUCT BACKLOG UPDATE", "2. DESIGN HANDOVER SESSION", ],
      paragraphs: [
        "In the Product Backlog Update, design-related information is added to the user stories. This includes linking relevant design assets, providing clarifications, and specifying the behavior of specific components. By incorporating design details into the user stories, we ensure that all necessary information is easily accessible to the development team. This promotes better alignment between design and implementation, enhancing the overall fidelity of the product.",

"The Design Handover Session takes place just before the development sprint begins. It involves presenting the validated designs to the development team and ensuring that all details and components are well described and clear for everyone. This direct communication between designers and developers allows for a smooth transfer of design-related information. Additionally, the session provides an opportunity for the development team to provide feedback and address any concerns they may have regarding the designs. This collaborative step fosters effective teamwork and facilitates a successful transition from design to development.",
      ],
    },
    
  }

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible")
            observer.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.1 },
    )

    if (headerTitleRef.current) observer.observe(headerTitleRef.current)
    if (topParagraphRef.current) observer.observe(topParagraphRef.current)
    if (mainDescriptionRef.current) observer.observe(mainDescriptionRef.current)
    if (imageRef.current) observer.observe(imageRef.current)
    if (tabsRef.current) observer.observe(tabsRef.current)

    return () => {
      if (headerTitleRef.current) observer.unobserve(headerTitleRef.current)
      if (topParagraphRef.current) observer.unobserve(topParagraphRef.current)
      if (mainDescriptionRef.current) observer.unobserve(mainDescriptionRef.current)
      if (imageRef.current) observer.unobserve(imageRef.current)
      if (tabsRef.current) observer.unobserve(tabsRef.current)
    }
  }, [])

  return (
    <div className="scoping-container" id="process">
      <div className="container">
      <div className="scoping-wrapper">
        {/* Top Section Grid: Title (left) and New Paragraph (right) */}
        <div className="intro-grid">
          <div className="header-title-wrapper animate-left" ref={headerTitleRef}>
            <h2 className="header-title4144">
              OUR
              <br />
              <span className="highlight">IDP</span>
              <br />
              <span className="highlight">PROCESS</span>
            </h2>
          </div>
          <div className="top-right-paragraph animate-left" ref={topParagraphRef}>
            <p>
             At Lattice Labs, we firmly believe in a user-centered approach to product design that combines innovation and practicality. We understand the significance of comprehending users’ goals, business requirements, and technical considerations in every project.
            </p>
          </div>

          {/* Main Content Grid: Long Description (left) and Image (right) */}
          <div className="main-description-wrapper animate-left" ref={mainDescriptionRef}>
            <p className="main-description">
              To fulfill this philosophy, we have developed the Incremental Design Process (IDP), a meticulously crafted and well-structured framework that brings together product owners, designers, developers, and managers. Our IDP is specifically designed to prioritize exceptional user experience (UX) and interaction design, while also ensuring a careful balance between costs and benefits.
By doing so, we aim to prevent unnecessary resource wastage and facilitate a prompt market launch for your product.
            </p>
          </div>
          <div className="image-section animate-right" ref={imageRef}>
            <img
              src="/process-scoping.webp?height=300&width=400"
              alt="Scoping Process Illustration"
              className="process-image"
            />
          </div>
        </div>

        {/* Tabs Section */}
        <div className="tabs-container" ref={tabsRef}>
          <div className="tabs-header">
            <button
              className={`tab-button ${activeTab === "research" ? "active" : ""}`}
              onClick={() => setActiveTab("research")}
            >
              <div className="tab-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                </svg>
              </div>
              BOOTSTRAP
            </button>
            <button
              className={`tab-button ${activeTab === "ideation" ? "active" : ""}`}
              onClick={() => setActiveTab("ideation")}
            >
              <div className="tab-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
                  <line x1="12" y1="7" x2="12" y2="17" />
                  <line x1="7" y1="12" x2="17" y2="12" />
                </svg>
              </div>
              DESIGN SPRINT 1...N
            </button>
            <button
              className={`tab-button ${activeTab === "execution" ? "active" : ""}`}
              onClick={() => setActiveTab("execution")}
            >
              <div className="tab-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              </div>
              BACKLOG
            </button>
            
          </div>

          <div className="tab-content">
            {Object.keys(tabContent).map((key) => {
              const content = tabContent[key]
              return (
                <div key={key} className={`tab-panel ${activeTab === key ? "active" : ""}`}>
                  <h3 className="tab-title">{content.title}</h3>
                  {content.listItems && (
                    <ul className="tab-list">
                      {content.listItems.map((item, index) => (
                        <li key={index} className="tab-item">
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}
                  {content.paragraphs && (
                    <>
                      {content.paragraphs.map((paragraph, index) => (
                        <p key={index} className="tab-description">
                          {paragraph}
                        </p>
                      ))}
                    </>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {/* CTA Section */}
        <div className="cta-section">
          <h3 className="cta-title">NEED A QUOTE FOR YOUR PROJECT?</h3>
          <p className="cta-description">
            Our team of business developers and project managers are ready to analyze your questions. Have a coffee,
            free time to explain us everything and get a quote for your project.
          </p>
          <button className="quote-button">GET A QUOTE</button>
        </div>
      </div>
      </div>
    </div>
  )
}

export default ProductDesignProcess
